import React, { Component } from "react";
import { message, Tag, Progress, Alert, Space } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import { ModalItem, ButtonItem, TableItem, InputItem, dineroDisabledMask } from "./Items";
import { MailOutlined } from "@ant-design/icons";

/**
 * Envío masivo de recibos de nómina por correo.
 *
 * Reutilizable desde PreNomina (una nómina, con líneas que pueden no estar
 * timbradas) y desde la consulta masiva de Recibos (histórico ya timbrado).
 *
 * Muestra a quién le va a llegar cada recibo con el correo del empleado
 * precargado y editable, y excluye las líneas sin correo o sin calcular.
 *
 * El envío corre en segundo plano en el servidor —cientos de empleados no caben
 * en el tiempo de una petición— y el avance se sigue por polling a
 * `prenomina/estadoProceso`, que se apoya en Redis y por eso funciona igual
 * detrás del balanceador sin necesitar un WebSocket propio.
 */

const INTERVALO_POLLING_MS = 2000;

// `estadoProceso.leer` también devuelve null cuando Redis no responde a tiempo (su timeout
// interno es de 2s), no solo cuando el proceso terminó. Con dos lecturas vacías el modal se
// rendía a los 4s y anunciaba "todos se enviaron correctamente" con cero enviados, mientras el
// lote seguía mandando correos en el servidor. 10 × 2s = 20s de tolerancia, el mismo presupuesto
// que PreNomina y seguirProcesoMasivo (10s × 2); aquí en ticks más cortos porque a diferencia de
// aquellos esto dibuja una barra que tiene que moverse.
const VACIOS_PARA_TERMINAR = 10;

const FASE = { SELECCION: "seleccion", ENVIANDO: "enviando", RESUMEN: "resumen" };

class ModalEnviarRecibos extends Component {
  temporizador = null;

  // Campo de instancia y no state: `setState` es asíncrono, así que dos ticks solapados leían el
  // mismo valor y el contador no avanzaba. Tampoco se dibuja. Misma solución que `_respaldoSinEstado`
  // de PreNomina y `_lecturasVacias` de seguirProcesoMasivo.
  _vaciosConsecutivos = 0;

  state = {
    loading: false,
    fase: FASE.SELECCION,
    recibos: [],
    seleccionados: [],
    progreso: null,
    // El envío terminó pero no hay cifras que reportar. Ver `terminar`.
    sinDatos: false,
    error: null,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible !== prevProps.visible && this.props.visible) {
      this.consultarCorreos();
    }
  }

  componentWillUnmount() {
    this.detenerPolling();
  }

  detenerPolling() {
    if (this.temporizador) {
      clearInterval(this.temporizador);
      this.temporizador = null;
    }
  }

  async consultarCorreos() {
    const { lineaIds, empresaId, headersToken } = this.props;
    this._vaciosConsecutivos = 0;
    this.setState({
      loading: true, fase: FASE.SELECCION, recibos: [], seleccionados: [],
      progreso: null, sinDatos: false, error: null,
    });
    try {
      const res = await axios.post(
        "prenomina/correosRecibosMasivo",
        { empresaId, lineaIds },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        const recibos = res.data.recibos;
        // Preseleccionar solo lo que sí se puede mandar: con recibo y con correo.
        const seleccionables = recibos
          .filter((r) => !r.sinRecibo && r.correo)
          .map((r) => r.lineaId);
        this.setState({ recibos, seleccionados: seleccionables, loading: false });
      } else {
        message.error(res.data.message || "Error al consultar los correos");
        this.setState({ loading: false });
      }
    } catch {
      message.error("Error al consultar los correos");
      this.setState({ loading: false });
    }
  }

  cambiarCorreo(lineaId, correo) {
    this.setState((prev) => ({
      recibos: prev.recibos.map((r) => (r.lineaId === lineaId ? { ...r, correo } : r)),
    }));
  }

  async enviar() {
    const { empresaId, headersToken } = this.props;
    const { recibos, seleccionados } = this.state;

    const destinatarios = recibos
      .filter((r) => seleccionados.includes(r.lineaId) && !r.sinRecibo)
      .map((r) => ({ lineaId: r.lineaId, correo: (r.correo || "").trim() }))
      .filter((d) => d.correo);

    if (destinatarios.length === 0) {
      message.info("No hay recibos con correo para enviar");
      return;
    }

    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "prenomina/enviarRecibosNominaMasivo",
        { empresaId, destinatarios },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        // Se reinicia también aquí: una segunda corrida arrancaría con el contador de la anterior.
        this._vaciosConsecutivos = 0;
        this.setState({
          loading: false,
          fase: FASE.ENVIANDO,
          sinDatos: false,
          progreso: { total: res.data.total, procesados: 0, enviados: 0, noEnviados: [], noEnviadosTotal: 0 },
        });
        this.temporizador = setInterval(() => this.consultarProgreso(), INTERVALO_POLLING_MS);
      } else {
        message.error(res.data.message || "No se pudo iniciar el envío");
        this.setState({ loading: false });
      }
    } catch {
      message.error("Error al iniciar el envío");
      this.setState({ loading: false });
    }
  }

  async consultarProgreso() {
    const { empresaId, headersToken, usuarioId } = this.props;
    try {
      const res = await axios.post(
        "prenomina/estadoProceso",
        { empresaId, tipo: "envioRecibos", referenciaId: usuarioId },
        { headers: headersToken }
      );
      if (res.data.success !== 2) return;

      const { estado, resultado } = res.data;

      if (!estado) {
        // Puede ser fin del proceso o una falla momentánea de Redis.
        this._vaciosConsecutivos += 1;
        if (this._vaciosConsecutivos < VACIOS_PARA_TERMINAR) return;
        // El resultado sobrevive al fin del proceso justamente para este camino. Si tampoco
        // está, el envío terminó SIN cifras: el `null` se lo dice a `renderResumen`.
        this.terminar(resultado || null);
        return;
      }

      this._vaciosConsecutivos = 0;
      this.setState({ progreso: estado });

      // El servidor guarda el resultado ANTES de publicar el fin, así que a estas alturas ya
      // está; se prefiere sobre `estado` porque son las cifras definitivas y no las del tick
      // que nos haya tocado leer.
      if (estado.evento === "envio_fin") {
        this.terminar(resultado || estado);
      } else if (estado.evento === "envio_error") {
        this.setState({ error: estado.mensaje || "Error durante el envío" });
        this.terminar(resultado || estado);
      }
    } catch {
      // Un tick que falla no aborta nada: el siguiente vuelve a intentar.
    }
  }

  /**
   * `resumen` en null significa: el proceso dejó de publicar y tampoco dejó cifras. No se puede
   * afirmar nada sobre el envío —el lote puede seguir corriendo en el servidor— y el resumen
   * tiene que decirlo. Antes se pintaba igual que un éxito.
   */
  terminar(resumen) {
    this.detenerPolling();
    this.setState({ fase: FASE.RESUMEN, progreso: resumen || null, sinDatos: !resumen });
    if (this.props.onEnviado) this.props.onEnviado();
  }

  cerrar() {
    this.detenerPolling();
    this._vaciosConsecutivos = 0;
    this.setState({
      fase: FASE.SELECCION, recibos: [], seleccionados: [],
      progreso: null, sinDatos: false, error: null,
    });
    this.props.onClose();
  }

  renderSeleccion() {
    const { recibos, seleccionados, loading } = this.state;

    const sinCorreo = recibos.filter((r) => !r.sinRecibo && !r.correo).length;
    const sinRecibo = recibos.filter((r) => r.sinRecibo).length;
    const aEnviar = recibos.filter(
      (r) => seleccionados.includes(r.lineaId) && !r.sinRecibo && (r.correo || "").trim()
    ).length;

    const columns = [
      {
        title: "Empleado", dataIndex: "nombreEmpleado", key: "nombreEmpleado",
        buscar: "nombreEmpleado",
      },
      { title: "Periodo", dataIndex: "prenominaNombre", key: "prenominaNombre" },
      {
        title: "Estatus", dataIndex: "estatus", key: "estatus", align: "center",
        render: (_, record) => (
          record.sinRecibo
            ? <Tag color="default" style={{ fontSize: 10 }}>Sin calcular</Tag>
            : record.timbrada
              ? <Tag color="purple" style={{ fontSize: 10 }}>Timbrada</Tag>
              : <Tag color="orange" style={{ fontSize: 10 }}>Sin timbrar</Tag>
        ),
      },
      {
        title: "Neto", dataIndex: "totalNeto", key: "totalNeto", align: "right",
        render: (v) => dineroDisabledMask(v || 0),
      },
      {
        title: "Correo destino", dataIndex: "correo", key: "correo",
        render: (v, record) => (
          <InputItem
            value={v}
            disabled={record.sinRecibo}
            placeholder={record.sinRecibo ? "—" : "Sin correo registrado"}
            onChange={(e) => this.cambiarCorreo(record.lineaId, e.target.value)}
          />
        ),
      },
    ];

    return (
      <>
        <div style={{ marginBottom: 12 }}>
          <Space size={6} wrap>
            <Tag color="blue">{aEnviar} por enviar</Tag>
            {sinCorreo > 0 && (
              <Tag color="red">{sinCorreo} sin correo registrado</Tag>
            )}
            {sinRecibo > 0 && (
              <Tag color="default">{sinRecibo} sin calcular (no se pueden enviar)</Tag>
            )}
          </Space>
          <p style={{ color: "#888", fontSize: 12, margin: "8px 0 0" }}>
            Cada recibo se manda al correo de su renglón; puedes corregirlo antes de
            enviar. Los renglones sin correo se omiten y se reportan al terminar.
          </p>
        </div>

        <TableItem
          rowKey={(v) => v.lineaId}
          columns={columns}
          dataSource={recibos}
          size="small"
          defaultPageSize={10}
          rowSelection={{
            selectedRowKeys: seleccionados,
            onChange: (keys) => this.setState({ seleccionados: keys }),
            getCheckboxProps: (record) => ({ disabled: record.sinRecibo }),
          }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <ButtonItem onClick={this.cerrar.bind(this)}>Cancelar</ButtonItem>
          <ButtonItem
            type="primary"
            icon={<MailOutlined />}
            loading={loading}
            disabled={aEnviar === 0}
            onClick={this.enviar.bind(this)}
          >
            Enviar {aEnviar} recibo(s)
          </ButtonItem>
        </div>
      </>
    );
  }

  renderEnviando() {
    const { progreso } = this.state;
    const total = progreso?.total || 0;
    const procesados = progreso?.procesados || 0;
    const porcentaje = total > 0 ? Math.round((procesados / total) * 100) : 0;

    return (
      <div style={{ padding: "10px 0" }}>
        <Progress percent={porcentaje} status="active" />
        <p style={{ textAlign: "center", color: "#555", marginTop: 12 }}>
          Enviando {procesados} de {total} recibos…
        </p>
        <p style={{ textAlign: "center", color: "#888", fontSize: 12 }}>
          {progreso?.enviados || 0} enviados · {progreso?.noEnviadosTotal || 0} no enviados
        </p>
        <p style={{ textAlign: "center", color: "#aaa", fontSize: 11, marginTop: 12 }}>
          Puedes cerrar esta ventana: el envío continúa en el servidor.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <ButtonItem onClick={this.cerrar.bind(this)}>Cerrar</ButtonItem>
        </div>
      </div>
    );
  }

  renderResumen() {
    const { progreso, error, sinDatos } = this.state;
    const noEnviados = progreso?.noEnviados || [];
    const noEnviadosTotal = progreso?.noEnviadosTotal || 0;
    // El detalle viene recortado desde el servidor cuando el lote es grande.
    const ocultos = noEnviadosTotal - noEnviados.length;

    return (
      <div style={{ padding: "10px 0" }}>
        {error ? (
          <Alert type="error" showIcon message="El envío terminó con errores" description={error} />
        ) : sinDatos ? (
          // Ni llave de progreso ni resultado: el servidor no contestó a tiempo, o el proceso
          // murió. Pintar verde aquí era el bug — decía "todos se enviaron correctamente" con
          // cero enviados y el lote todavía corriendo.
          <Alert
            type="warning"
            showIcon
            message="No se pudo confirmar el resultado del envío"
            description="Se perdió el contacto con el proceso. El envío pudo haber terminado o seguir corriendo en el servidor; revisa Historial de Correos para ver qué salió y qué no."
          />
        ) : (
          <Alert
            type={noEnviadosTotal > 0 ? "warning" : "success"}
            showIcon
            message={`${progreso?.enviados || 0} recibo(s) enviado(s)`}
            description={
              noEnviadosTotal > 0
                ? `${noEnviadosTotal} no se enviaron. El detalle también queda en Historial de Correos.`
                : "Todos los recibos se enviaron correctamente."
            }
          />
        )}

        {noEnviados.length > 0 && (
          <div style={{ marginTop: 16, maxHeight: 260, overflowY: "auto" }}>
            <h4 style={{ marginBottom: 8 }}>No se enviaron:</h4>
            {noEnviados.map((o, i) => (
              <div
                key={i}
                style={{
                  fontSize: 12, padding: "6px 8px",
                  borderBottom: "1px solid #f0f0f0", display: "flex", gap: 8,
                }}
              >
                <span style={{ flex: 1, fontWeight: 500 }}>{o.nombreEmpleado || "—"}</span>
                <span style={{ color: "#888" }}>{o.correo || "sin correo"}</span>
                <span style={{ color: "#a32d2d" }}>{o.motivo}</span>
              </div>
            ))}
            {ocultos > 0 && (
              <p style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
                y {ocultos} más — consúltalos en Historial de Correos.
              </p>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <ButtonItem type="primary" onClick={this.cerrar.bind(this)}>Cerrar</ButtonItem>
        </div>
      </div>
    );
  }

  render() {
    const { visible } = this.props;
    const { loading, fase } = this.state;

    const titulos = {
      [FASE.SELECCION]: "Enviar recibos por correo",
      [FASE.ENVIANDO]:  "Enviando recibos…",
      [FASE.RESUMEN]:   "Resumen del envío",
    };

    return (
      <ModalItem
        title={titulos[fase]}
        visible={visible}
        spinning={loading && fase === FASE.SELECCION}
        onCancel={this.cerrar.bind(this)}
        paddingTop
        width={fase === FASE.SELECCION ? 900 : 560}
      >
        {fase === FASE.SELECCION && this.renderSeleccion()}
        {fase === FASE.ENVIANDO  && this.renderEnviando()}
        {fase === FASE.RESUMEN   && this.renderResumen()}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  usuarioId: state.user.usuarioId,
});

export default connect(mapStateToProps)(ModalEnviarRecibos);
